import { useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';

import UpdateBulkDnrMutation from '@/graphql/contracts/updateBulkDnr.mutation.gql';
import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { UpdateBulkDnr, UpdateBulkDnrVariables } from '@/graphql/contracts/_generated_/updateBulkDnr.mutation';
import { notificationService } from '@/services/notification/notification.client';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import {
  triggerLineRefresh
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { triggerVariableRefresh } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import GetPaginatedFilteredVariableContractLinesQuery from
  '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { ALL_ITEMS } from '@/config/common.config';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';

type DNRContractLineDataType = {
  contractLineID: UUID;
  dnrDate: Date;
  reasonCode: string;
  comment: string;
  noFurtherBilling: boolean;
  noFurtherBillingDate: Date | null | undefined;
};

export type UpdateBulkDnrDto = {
  contractId: UUID;
  dnrContractLineData: DNRContractLineDataType[];
};

export default function useUpdateBulkDnr() {
  const { contractId }: { contractId: UUID } = useParams();

  const [_updateBulkDnr, { loading }] = useMutation<UpdateBulkDnr, UpdateBulkDnrVariables>(
    UpdateBulkDnrMutation,
    {
      client: apolloClientContracts,
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
      awaitRefetchQueries: true,
      onCompleted: () => {
        triggerLineRefresh();
        triggerVariableRefresh();
        notificationService.open({
          title: 'Contract Line Status Saved',
          text: 'The chosen contract lines have been changed',
          type: 'success'
        });
      }
    }
  );

  const updateBulkDnr = (input: UpdateBulkDnrDto) => {
    _updateBulkDnr({
      variables: input,
      refetchQueries: [
        {
          query: GetContractDetailsQuery,
          variables: { contractId }
        },
        {
          query: GetBCContractLinesQuery,
          variables: {
            filter: {
              contractId: { eq: contractId }
            },
            first: ALL_ITEMS
          }
        },
        {
          query: GetPaginatedFilteredVariableContractLinesQuery,
          variables: {
            filter: { headerPrimaryKey: { eq: contractId } },
            first: ALL_ITEMS,
          }
        }
      ]
    });
  };

  return {
    updateBulkDnr,
    isLoadingUpdateBulkDnr: loading,
  } as const;
}
